// @ts-nocheck
import { useParams, useNavigate } from 'react-router-dom';
import { YStack, XStack, Text, Paragraph } from 'tamagui';
import { Container } from '../../components/Container';
import { PageLayout } from '../../components/PageLayout';
import { WaitlistForm } from '../../components/WaitlistForm';
import { SplitRow } from '../../components/SplitRow';
import { TorchVignette, StatioVignette, DenariusVignette, LedgerVignette, PilumVignette, ScutaVignette, VigilVignette } from '../../components/ProductVignettes';
import { useIsMobile } from '../../hooks/useIsMobile';

const products = {
  torch: {
    name: 'Torch',
    numeral: 'I',
    latin: 'FAX · THE LIGHT',
    tagline: 'Your work, one search away',
    accent: '#d4944a',
    status: 'DEPLOYED',
    url: 'https://usetorch.dev',
    hero: 'Stop switching between apps. Torch connects every tool your team uses into one searchable, productive workspace.',
    sections: [
      {
        title: 'The Problem',
        content: 'Your work lives in Gmail, Slack, Notion, Google Drive, GitHub, Jira, and a dozen other tools. The document you need is always in the app you\'re not looking at. You end up with 15 tabs open, searching the same query in three different places. You\'re paying $30-80/month for separate subscriptions to a project tracker, a calendar, a docs tool, a wiki, and a notes app.',
      },
      {
        title: 'The Solution',
        content: 'Torch gives you one search bar that spans every tool you use. Type once, find anything — emails, Slack messages, Notion pages, Drive files, GitHub issues. No more context switching. And search is just the start. Torch includes built-in Kanban boards, Calendar, Documents, Wiki, OKRs, Sprints, and Announcements — all in the same workspace.',
      },
    ],
    features: [
      { title: 'Unified Search', desc: 'Search across Gmail, Slack, Notion, Google Drive, GitHub, Jira, and 50+ integrations from one search bar.' },
      { title: 'Built-in Productivity Suite', desc: 'Kanban, Calendar, Documents, Wiki, OKRs, Sprints, and Announcements — no more paying for separate tools.' },
      { title: 'Cross-Ecosystem', desc: 'Works across Microsoft, Google, and Slack simultaneously. Not locked to one vendor.' },
      { title: 'Desktop & Mobile', desc: 'Web, desktop (Tauri), and mobile (React Native) clients. Your workspace goes where you go.' },
    ],
    differentiators: [
      { vs: 'Glean', point: 'Glean targets 500+ employee enterprises at enterprise prices. Torch is accessible to individuals and small teams with built-in productivity apps Glean doesn\'t have.' },
      { vs: 'Notion + Linear + Calendar', point: 'Replace 3-5 separate subscriptions with one workspace. Same features, one login, one search.' },
    ],
    pricing: 'Free tier with 3 apps. Pro at $14/user/mo. Team at $22/user/mo.',
    cta: 'Try Torch',
  },
  statio: {
    name: 'Statio',
    numeral: 'II',
    latin: 'STATIO · THE CHECKPOINT',
    tagline: 'The checkpoint for AI agents',
    accent: '#5a7090',
    status: 'MUSTERING',
    url: 'https://statio.dev',
    hero: 'Your AI agents call APIs at machine speed with zero governance. Statio is the security layer that was missing.',
    sections: [
      {
        title: 'The Problem',
        content: 'Every AI agent in your stack has API keys scattered across config files. There\'s no audit trail for what your agents are doing, no access policies controlling which APIs they can call, and no way to rotate credentials without breaking everything. When — not if — a key leaks, you have no idea what it accessed.',
      },
      {
        title: 'The Solution',
        content: 'Statio sits between your AI agents and your APIs. Every credential is vaulted — agents never see raw API keys. Every call is logged with full audit trails. Access policies define exactly which agents can call which APIs with what permissions. One STATIO_TOKEN replaces dozens of scattered keys.',
      },
    ],
    features: [
      { title: 'Credential Vaulting', desc: 'API keys stored in encrypted vaults with automatic rotation. Agents only see a single token.' },
      { title: 'Access Policies', desc: 'RBAC for AI agents — define who can call what, with what permissions, at what rate.' },
      { title: 'Full Audit Trail', desc: 'Every API call logged with agent identity, timestamp, request/response, and customer context.' },
      { title: 'MCP Gateway', desc: 'Multi-tenant gateway with JWT auth, rate limiting, and usage-based billing built in.' },
      { title: 'mcp-gen CLI', desc: 'Generate MCP servers from any OpenAPI spec. Your API becomes AI-accessible in minutes.' },
      { title: 'Pre-built Catalog', desc: '50+ pre-built MCP server templates for Stripe, GitHub, Slack, and more.' },
    ],
    differentiators: [
      { vs: 'DIY', point: 'Building MCP security yourself means months of work on auth, vaulting, logging, and multi-tenancy. Statio does it in one integration.' },
      { vs: 'Raw MCP', point: 'MCP alone has no security model. Statio adds the governance layer — credentials, policies, and audit trails.' },
    ],
    pricing: 'Free with 3 servers and 5,000 calls. Developer $19/mo, Team $99/mo, Business $349/mo.',
    cta: 'Join Waitlist',
  },
  denarius: {
    name: 'Denarius',
    numeral: 'III',
    latin: 'DENARIUS · THE COIN',
    tagline: 'Bloomberg insights, not Bloomberg prices',
    accent: '#34d399',
    status: 'FORGING',
    hero: 'Institutional-grade financial analytics for retail traders and developers. Automated data extraction from SEC filings, real-time signal streams, and portfolio analysis.',
    sections: [
      {
        title: 'The Problem',
        content: 'Bloomberg charges $25K/year. Retail traders and independent developers can\'t access institutional-grade financial analysis. The data exists in SEC filings — 10-Ks, 10-Qs, 8-Ks — but extracting structured data from them is painful, manual work.',
      },
      {
        title: 'The Solution',
        content: 'Denarius automates multi-tier extraction from SEC filings — XBRL API first, HTML scraping fallback. Debt schedules, equity compensation, financial statements — all structured and queryable. A portfolio analytics dashboard gives you TWR, IRR, Sharpe, drawdown, beta, and alpha. A developer API gives you programmatic access to everything.',
      },
    ],
    features: [
      { title: 'SEC Filing Extraction', desc: 'Automated extraction from 10-K, 10-Q, 8-K filings via EDGAR. XBRL first, HTML fallback.' },
      { title: 'Portfolio Analytics', desc: 'TWR, IRR, Sharpe ratio, max drawdown, beta, alpha, dividend tracking, and tax reporting.' },
      { title: 'Signal Stream', desc: 'Real-time signals from EDGAR filings, FMP market data, XBRL extractions, and macro indicators.' },
      { title: 'Developer API', desc: 'Programmatic access to structured filing data and market fundamentals.' },
      { title: 'MCP-Ready', desc: 'Serve financial data as MCP tools for AI agents and LLM workflows.' },
    ],
    differentiators: [
      { vs: 'Bloomberg Terminal', point: 'Same depth of analysis at a fraction of the cost. API-first, not terminal-first.' },
      { vs: 'Yahoo Finance', point: 'Denarius extracts primary source data from SEC filings. Yahoo shows you what someone else extracted.' },
    ],
    pricing: 'Free tier with 1 portfolio. Investor at $24/mo. Developer at $59/mo.',
    cta: 'Coming Soon',
  },
  janusledger: {
    name: 'JanusLedger',
    numeral: 'IV',
    latin: 'IANUS · BOTH SIDES',
    tagline: 'Stop building your own ledger',
    accent: '#60a5fa',
    status: 'FORGING',
    hero: 'Production-ready double-entry accounting infrastructure. Ship your fintech app months faster and pass audits on day one.',
    sections: [
      {
        title: 'The Problem',
        content: 'Every fintech that moves money needs a double-entry ledger. Every team builds it from scratch — and gets it wrong the first time. Unbalanced entries, missing audit trails, currency precision errors, failed reconciliation. It takes months to build and years to trust.',
      },
      {
        title: 'The Solution',
        content: 'JanusLedger is a programmable ledger API with guaranteed balanced entries, immutable audit trails, and multi-currency support out of the box. Designed from firsthand experience building double-entry accounting systems at scale in production fintech environments.',
      },
    ],
    features: [
      { title: 'True Double-Entry', desc: 'Every transaction guaranteed balanced. Debits always equal credits. No exceptions.' },
      { title: 'Multi-Currency', desc: 'ISO 4217 currency codes with proper decimal precision. Handle any currency correctly.' },
      { title: 'Immutable Audit Trail', desc: 'Every entry is permanent and traceable. Pass SOC2, PCI, and financial audits.' },
      { title: 'Bank Reconciliation', desc: 'Automated reconciliation with drift detection and alerting.' },
      { title: 'Health Monitoring', desc: 'Continuous validation of ledger integrity with real-time alerting.' },
    ],
    differentiators: [
      { vs: 'Modern Treasury', point: 'Better price point, superior developer experience. Built by someone who\'s actually done this at scale.' },
      { vs: 'Building In-House', point: 'Skip 6+ months of ledger development. Focus on your product, not your accounting infrastructure.' },
    ],
    pricing: '30-day free trial. Starter at $149/mo. Growth at $349/mo.',
    cta: 'Coming Soon',
  },
  pilum: {
    name: 'Pilum',
    numeral: 'V',
    latin: 'PILUM · THE JAVELIN',
    tagline: 'One config, every cloud',
    accent: '#fb923c',
    url: 'https://pilum.dev',
    hero: 'Open-source multi-cloud deployment CLI. Write a pilum.yaml, deploy to any provider. We built it to ship our own products — now it ships yours too.',
    sections: [
      {
        title: 'The Problem',
        content: 'Deploying to multiple cloud providers means multiple CI/CD configs, multiple CLIs, multiple authentication flows. Each provider has its own deployment model, its own gotchas, and its own way of doing things.',
      },
      {
        title: 'The Solution',
        content: 'Pilum gives you one config file and one CLI for every cloud provider. Define your service once in a pilum.yaml, deploy to Cloud Run, Lambda, Azure Container Apps, Cloudflare Pages, npm, Homebrew, or Docker Hub. Recipe-driven workflows with parallel execution and monorepo support.',
      },
    ],
    features: [
      { title: '7 Deploy Targets', desc: 'GCP Cloud Run, AWS Lambda, Azure Container Apps, Cloudflare Pages, npm, Homebrew, Docker Hub.' },
      { title: 'Recipe-Driven', desc: 'Composable recipes with ingredients and handlers. Build your own deployment workflows.' },
      { title: 'Parallel Execution', desc: 'Wave-based deployment — independent services deploy simultaneously.' },
      { title: 'Monorepo Support', desc: 'Deploy multiple services from a single repo with one command.' },
    ],
    differentiators: [
      { vs: 'GoReleaser', point: 'GoReleaser is Go-only releases. Pilum is any-language, any-cloud deployment.' },
      { vs: 'Manual CI/CD', point: 'One pilum.yaml replaces dozens of GitHub Actions configs across multiple providers.' },
    ],
    pricing: 'Free and open source. BSL 1.1 → Apache 2.0 in 2028.',
    cta: 'View on GitHub',
    oss: true,
  },
  capsa: {
    name: 'Capsa',
    numeral: 'VII',
    latin: 'CAPSA · THE SCROLL CASE',
    tagline: 'Documentation you own',
    accent: '#60a5fa',
    status: 'DEPLOYED',
    oss: true,
    url: 'https://capsa.romans.dev',
    hero: 'A self-hosted documentation platform. Fast as in Vite, free as in libre — you own the code and the hosting bill is zero.',
    sections: [
      {
        title: 'The Problem',
        content: 'Hosted documentation platforms charge per seat for something that compiles to static HTML. You write your docs in their editor, in their format, on their domain, and when you want to leave you discover the export is a zip of HTML nobody can maintain. The alternative — wiring up a static site generator, search, an OpenAPI viewer and social cards yourself — is a week you did not plan for.',
      },
      {
        title: 'The Solution',
        content: 'Capsa renders MDX into a documentation site with command-palette search, a themed OpenAPI reference, AI-friendly exports and social cards already wired up. It prerenders to static HTML and deploys to Cloudflare Pages or any static host for nothing. It is a Vite, React and Tamagui app you fully own and can edit.',
      },
    ],
    features: [
      { title: 'MDX Content', desc: 'Write in Markdown with components where you need them. No proprietary editor, no lock-in.' },
      { title: 'Command Palette Search', desc: 'Full-text search across every page, keyboard-first, with no external search service to pay for.' },
      { title: 'OpenAPI Reference', desc: 'Point it at a spec and get a themed API reference that matches the rest of the site.' },
      { title: 'Prerendered Static HTML', desc: 'Every route ships as static HTML, so crawlers and social cards see real content.' },
      { title: 'AI-Friendly Exports', desc: 'Copy-page actions and llms.txt output, so assistants read your docs accurately.' },
      { title: 'Free Hosting', desc: 'Deploys to Cloudflare Pages or any static host. No per-seat pricing, ever.' },
    ],
    differentiators: [
      { vs: 'Hosted docs platforms', point: 'They charge per seat to serve static files and own your content. Capsa is Apache 2.0 — your repo, your host, your domain.' },
      { vs: 'Rolling your own', point: 'Search, OpenAPI rendering, theming, social cards and prerendering are already solved. Start at writing, not at plumbing.' },
    ],
    pricing: 'Free and open source. Apache 2.0.',
    cta: 'View on GitHub',
  },
  vigil: {
    name: 'Vigil',
    numeral: 'VII',
    latin: 'VIGIL · THE WATCH',
    tagline: 'Watch when your ISP and property manager won\'t',
    accent: '#e0a458',
    status: 'DEPLOYED',
    url: 'https://github.com/SID-Technologies/Vigil/releases/latest',
    hero: 'Network reliability evidence, on demand. Vigil watches your connection 24/7 and generates the uptime, latency, and outage records you need to push back when your ISP or building staff give you the runaround.',
    sections: [
      {
        title: 'The Problem',
        content: 'Your internet drops three times a day. Your ISP says "looks fine on our end." Your property manager says "we don\'t see any issues." You have nothing but frustration — no timestamps, no uptime percentages, no record of when it actually broke. Without evidence, your complaint is just a feeling.',
      },
      {
        title: 'The Solution',
        content: 'Vigil runs in the background and probes 13 endpoints every 2.5 seconds: public DNS, Teams/Zoom/Outlook over TCP, STUN servers, and your local gateway. Outages are detected live (3+ consecutive failures), aggregated to tiers (raw / 1-min / 5-min / 1-hour), and exported as HTML, CSV, or JSON reports — proof you can attach to a support ticket or send to building management.',
      },
    ],
    features: [
      { title: 'Continuous Probing', desc: 'ICMP + TCP + UDP-DNS + UDP-STUN to 13 default endpoints every 2.5 seconds. Add your own targets too.' },
      { title: 'Live Outage Detection', desc: '3-fail threshold with 60s recovery hold-off and 2-min coalesce window — no false alarms, no fragmented incidents.' },
      { title: 'Service Grouping', desc: 'Correlated failures across providers (e.g. STUN blocked across Google + Cloudflare) read as one incident, not many.' },
      { title: 'Wi-Fi Correlation', desc: 'Signal strength tracked alongside probes so you can prove "the dropouts line up with the signal drops."' },
      { title: 'Tiered Aggregation', desc: 'Raw samples for 7 days, 1-min buckets for 14 days, 5-min for 90 days, 1-hour forever. SQLite stays small over months of use.' },
      { title: 'Evidence Reports', desc: 'Generate self-contained HTML, CSV, or JSON exports of any window. Self-contained, shareable, audit-ready.' },
    ],
    differentiators: [
      { vs: 'Smokeping', point: 'Smokeping is a sysadmin tool — config files, RRD databases, perl scripts. Vigil is a desktop app that runs itself.' },
      { vs: 'PingPlotter', point: 'PingPlotter is paid software and Windows-leaning. Vigil is open source, cross-platform (macOS signed + Windows + Linux), and respects your data — everything stays local.' },
    ],
    pricing: 'Free and open source. Apache 2.0.',
    cta: 'Download',
    oss: true,
  },
  scuta: {
    name: 'Scuta',
    numeral: 'VI',
    latin: 'SCUTA · THE SHIELD',
    tagline: 'Managed CLI distribution',
    accent: '#4ade80',
    hero: 'Registry-driven CLI tool manager. Install, update, and enforce versions across your team with checksum verification and policy enforcement.',
    sections: [
      {
        title: 'The Problem',
        content: 'Your team has 5+ internal CLI tools. New hires spend an hour installing them. Someone\'s running an old version that has a bug you fixed months ago. There\'s no way to enforce that everyone\'s on the right version.',
      },
      {
        title: 'The Solution',
        content: 'Scuta is a registry-driven installer that manages your internal CLI tools. One command installs everything with SHA-256 checksum verification, parallel downloads, and version pinning. Policy enforcement ensures everyone\'s on the right version.',
      },
    ],
    features: [
      { title: 'Registry-Driven', desc: 'Central registry of all your team\'s CLI tools with version metadata.' },
      { title: 'Checksum Verification', desc: 'SHA-256 verification on every install. No supply chain surprises.' },
      { title: 'Parallel Installs', desc: 'Download and install multiple tools simultaneously.' },
      { title: 'Policy Enforcement', desc: 'Pin versions, enforce minimums, block deprecated tools.' },
    ],
    differentiators: [
      { vs: 'Homebrew', point: 'Homebrew is great for public packages. Scuta is for your private internal tools with policy enforcement.' },
      { vs: 'Manual Scripts', point: 'Replace your install.sh with a real tool that handles versions, checksums, and rollbacks.' },
    ],
    pricing: 'Free and open source.',
    cta: 'Coming Soon',
    oss: true,
  },
};

/**
 * Section shell shared by every band on this page, so a product page reads with
 * the same rhythm as the home page: a rule-and-label eyebrow, a display heading,
 * and full-width alternating grounds rather than a single narrow column.
 */
function Section({ eyebrow, title, lede, accent, tint, children, isMobile }) {
  return (
    <YStack
      paddingVertical={isMobile ? '$9' : '$11'}
      backgroundColor={tint ? '$color2' : '$background'}
    >
      <Container>
        <YStack gap={isMobile ? '$6' : '$8'}>
          <YStack gap="$4">
            <XStack alignItems="center" gap="$4">
              <YStack width={40} height={1} backgroundColor={accent} />
              <Text
                fontSize="$2"
                color="$color7"
                textTransform="uppercase"
                letterSpacing={3}
                fontWeight="500"
              >
                {eyebrow}
              </Text>
            </XStack>

            {title && (
              <Text
                fontSize={isMobile ? 30 : 40}
                fontWeight="700"
                letterSpacing={-1.4}
                lineHeight={isMobile ? 36 : 46}
                color="$color12"
                maxWidth={620}
              >
                {title}
              </Text>
            )}

            {lede && (
              <Paragraph
                fontSize={isMobile ? '$5' : '$6'}
                color="$color8"
                lineHeight={isMobile ? 26 : 30}
                maxWidth={620}
              >
                {lede}
              </Paragraph>
            )}
          </YStack>

          {children}
        </YStack>
      </Container>
    </YStack>
  );
}

const VIGNETTES = {
  torch: TorchVignette,
  statio: StatioVignette,
  denarius: DenariusVignette,
  janusledger: LedgerVignette,
  pilum: PilumVignette,
  scuta: ScutaVignette,
  vigil: VigilVignette,
};

export default function ProductPage({ theme, toggleTheme }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const product = products[slug];

  if (!product) {
    return (
      <PageLayout theme={theme} toggleTheme={toggleTheme}>
        <Container>
          <YStack paddingVertical="$12" alignItems="center" gap="$4">
            <Text fontSize="$9" fontWeight="700" color="$color12">404</Text>
            <Text fontSize="$4" color="$color7">That product does not exist.</Text>
            <Text
              fontSize="$3" color="$color8" cursor="pointer"
              hoverStyle={{ color: '$color11' }}
              onPress={() => navigate('/')}
            >
              Back to the portfolio
            </Text>
          </YStack>
        </Container>
      </PageLayout>
    );
  }

  const accent = product.accent;
  const Vignette = VIGNETTES[slug];
  // Eyebrow carries the gloss only. The watermark behind the hero already states
  // the name, and the Latin term alongside it made the same line twice.
  const gloss = (() => {
    const parts = product.latin.split('·').map((part) => part.trim());
    return parts.length > 1 ? parts.slice(1).join(' · ') : product.latin;
  })();
  const [problem, solution] = product.sections;

  return (
    <PageLayout theme={theme} toggleTheme={toggleTheme}>

      {/* Hero — text one side, the product's own vignette the other. */}
      <SplitRow
        eyebrow={gloss}
        title={product.tagline}
        body={product.hero}
        accent={accent}
        watermark={product.name}
        visual={Vignette ? <Vignette /> : null}
      />

      {/* Problem and answer as alternating rows — the pattern that keeps a long
          page moving without repeating a single column down the screen. */}
      {problem && (
        <SplitRow
          eyebrow="Why it exists"
          title="What is broken today."
          body={problem.content}
          accent={accent}
          reverse
          tint
          visual={
            <img
              src={`/art/${slug}.svg`}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: 'auto', maxHeight: 460, objectFit: 'cover', opacity: 0.9 }}
            />
          }
        />
      )}

      {solution && (
        <SplitRow
          eyebrow="The answer"
          title={`What ${product.name} does.`}
          body={solution.content}
          accent={accent}
          // No second visual: the vignette already appeared in the hero, and
          // repeating it reads as padding. The row collapses to one column.
        />
      )}

      {/* Pillar two — the hairline grid from the home-page roster. Cells sit on a
          1px rule-coloured ground so the gaps become the rules. */}
      <Section
        eyebrow="What you get"
        title="Everything in the box."
        accent={accent}
        isMobile={isMobile}
      >
        <XStack flexWrap="wrap" gap={1} backgroundColor="$color3" borderWidth={1} borderColor="$color3">
          {product.features.map((feature) => (
            <YStack
              key={feature.title}
              gap="$2"
              padding={isMobile ? '$4' : '$5'}
              backgroundColor="$background"
              flexGrow={1}
              flexBasis={isMobile ? '100%' : 280}
            >
              <Text fontSize={isMobile ? '$5' : '$6'} fontWeight="600" color="$color12">
                {feature.title}
              </Text>
              <Text fontSize="$3" color="$color8" lineHeight={21}>
                {feature.desc}
              </Text>
            </YStack>
          ))}
        </XStack>
      </Section>

      {/* Objection handling, in the slot the home page gives the FAQ. */}
      <Section
        eyebrow="How it compares"
        title="Why not the alternative."
        accent={accent}
        tint
        isMobile={isMobile}
      >
        <YStack maxWidth={860}>
          {product.differentiators.map((diff, i) => (
            <XStack
              key={diff.vs}
              flexDirection={isMobile ? 'column' : 'row'}
              gap={isMobile ? '$2' : '$6'}
              paddingVertical={isMobile ? '$4' : '$5'}
              borderTopWidth={1}
              borderBottomWidth={i === product.differentiators.length - 1 ? 1 : 0}
              borderColor="$color3"
              alignItems={isMobile ? 'flex-start' : 'baseline'}
            >
              <Text
                fontSize="$4"
                fontWeight="600"
                color={accent}
                minWidth={isMobile ? undefined : 160}
              >
                vs {diff.vs}
              </Text>
              <Paragraph fontSize="$4" color="$color8" lineHeight={24} flex={1}>
                {diff.point}
              </Paragraph>
            </XStack>
          ))}
        </YStack>
      </Section>

      {/* Pricing — a band, not an outlined box. Published pricing is a position,
          so it gets the same weight as everything else on the page. */}
      <Section
        eyebrow="Pricing"
        title={product.pricing}
        accent={accent}
        isMobile={isMobile}
      >
        {product.url && (
          <XStack>
            <Text
              fontSize="$5" fontWeight="600" color="$color1"
              backgroundColor={accent}
              paddingHorizontal="$6" paddingVertical="$4"
              cursor="pointer"
              hoverStyle={{ opacity: 0.88 }}
              onPress={() => window.open(product.url, '_blank')}
            >
              {product.cta}
            </Text>
          </XStack>
        )}
      </Section>

      <Section
        eyebrow="Stay posted"
        title={`Follow ${product.name}.`}
        lede={`Occasional mail when ${product.name} ships something worth knowing about. Nothing else.`}
        accent={accent}
        tint
        isMobile={isMobile}
      >
        <YStack maxWidth={420}>
          <WaitlistForm
            product={slug}
            source="romans.dev"
            accent={accent}
            buttonText={product.url ? 'Stay updated' : 'Join waitlist'}
          />
        </YStack>
      </Section>

    </PageLayout>
  );
}
